export interface DevotionalData {
  sermonTitle: string;
  scripture: string;
  date?: string;
  devotionalTitle: string;
  theme: string;
  keyVerse: string;
  mainDevotion: string;
  memoryVerse: {
    reference: string;
    text: string;
    motionTip: string;
    melodyHint: string;
  };
  objectLesson: {
    item: string;
    howTo: string;
    connection: string;
  };
  parentalGuide: {
    littleOnes: string;
    children: string;
    tweens: string;
  };
  wonderQuestions: Array<{ age: string; question: string }>;
  familyApplication: {
    tableQuestion: string;
    weeklyChallenge: string;
    conversationStarters: string[];
    prayerGuide: string;
  };
  parentalBlessing: string;
  closingThought: string;
}

function esc(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function paragraphs(text: string): string {
  return esc(text)
    .split(/\n\n+/)
    .filter(p => p.trim())
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('\n');
}

export function generateDevotionalHTML(data: DevotionalData): string {
  const {
    sermonTitle, scripture, date, devotionalTitle, theme, keyVerse,
    mainDevotion, memoryVerse, objectLesson, parentalGuide,
    wonderQuestions, familyApplication, parentalBlessing, closingThought
  } = data;

  const displayDate = date
    ? new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  const wonderRows = (wonderQuestions || []).map(q => `
    <div class="wonder-item">
      <span class="wonder-badge">${esc(q.age)}</span>
      <p class="wonder-q">${esc(q.question)}</p>
    </div>`).join('');

  const starters = (familyApplication.conversationStarters || [])
    .map(s => `<li>${esc(s)}</li>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Family Devotional — ${esc(devotionalTitle)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Crimson+Text:ital,wght@0,400;0,600;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <style>
    :root {
      --parchment:   #FDFAF3;
      --parchment-2: #F5EFE0;
      --forest:      #1E3A2F;
      --forest-mid:  #2D5240;
      --gold:        #A07820;
      --gold-light:  #C49A30;
      --gold-pale:   #F5EDD0;
      --rust:        #7A2C2C;
      --sage:        #5B7A6E;
      --ink:         #1A1A1A;
      --ink-mid:     #3A3A3A;
      --ink-light:   #666;
      --divider:     #D4C4A0;
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Crimson Text', Georgia, serif;
      background: var(--parchment);
      color: var(--ink);
      font-size: 18px;
      line-height: 1.7;
    }

    /* ── Cover ─────────────────────────────── */
    .cover {
      background: var(--forest);
      color: white;
      text-align: center;
      padding: 3.5rem 2rem 3rem;
      position: relative;
      overflow: hidden;
    }
    .cover::before, .cover::after {
      content: '';
      position: absolute;
      border-radius: 50%;
      background: rgba(255,255,255,0.04);
    }
    .cover::before { width: 500px; height: 500px; top: -200px; left: -150px; }
    .cover::after  { width: 400px; height: 400px; bottom: -180px; right: -100px; }
    .cover-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.25em;
      text-transform: uppercase;
      color: var(--gold-light);
      margin-bottom: 1.4rem;
    }
    .cover-ornament {
      font-size: 1.4rem;
      color: var(--gold-light);
      opacity: 0.7;
      display: block;
      margin-bottom: 0.8rem;
    }
    .cover h1 {
      font-family: 'Playfair Display', serif;
      font-size: clamp(1.8rem, 5vw, 3rem);
      font-weight: 700;
      line-height: 1.2;
      color: white;
      margin-bottom: 1rem;
      position: relative;
      z-index: 1;
    }
    .cover-theme {
      font-family: 'Crimson Text', serif;
      font-style: italic;
      font-size: 1.15rem;
      color: rgba(255,255,255,0.75);
      margin-bottom: 1.6rem;
    }
    .cover-verse {
      font-family: 'Crimson Text', serif;
      font-size: 1.05rem;
      color: var(--gold-light);
      font-style: italic;
      max-width: 520px;
      margin: 0 auto 1.8rem;
      border-top: 1px solid rgba(255,255,255,0.15);
      border-bottom: 1px solid rgba(255,255,255,0.15);
      padding: 0.8rem 1rem;
      position: relative;
      z-index: 1;
    }
    .cover-meta {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.72rem;
      font-weight: 500;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.45);
    }
    .cover-meta span { color: rgba(255,255,255,0.3); margin: 0 0.5rem; }

    /* ── Layout ─────────────────────────────── */
    .page { max-width: 780px; margin: 0 auto; padding: 3rem 2rem 4rem; }

    /* ── Divider ─────────────────────────────── */
    .ornament-divider {
      text-align: center;
      color: var(--divider);
      font-size: 1.2rem;
      letter-spacing: 0.4em;
      margin: 2.5rem 0;
      user-select: none;
    }

    /* ── Section headers ─────────────────────── */
    .section-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--sage);
      display: block;
      margin-bottom: 0.35rem;
    }
    h2.section-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.55rem;
      font-weight: 700;
      color: var(--forest);
      margin-bottom: 1.2rem;
      line-height: 1.25;
    }
    h3.sub-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--forest-mid);
      margin-bottom: 0.6rem;
    }

    /* ── Main devotion ───────────────────────── */
    .devotion-body p {
      margin-bottom: 1.1rem;
      text-align: justify;
      hyphens: auto;
    }
    .devotion-body p:first-child::first-letter {
      font-family: 'Playfair Display', serif;
      font-size: 4.2rem;
      font-weight: 700;
      color: var(--forest);
      float: left;
      line-height: 0.82;
      margin-right: 0.1em;
      margin-top: 0.12em;
    }

    /* ── Memory verse ────────────────────────── */
    .memory-verse-box {
      background: var(--forest);
      color: white;
      border-radius: 12px;
      padding: 2rem 2.2rem;
      margin: 2rem 0;
      position: relative;
    }
    .memory-verse-box .mv-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--gold-light);
      display: block;
      margin-bottom: 0.9rem;
    }
    .memory-verse-box blockquote {
      font-family: 'Playfair Display', serif;
      font-size: 1.25rem;
      font-style: italic;
      line-height: 1.5;
      color: white;
      margin-bottom: 0.5rem;
    }
    .memory-verse-box cite {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--gold-light);
      font-style: normal;
      display: block;
      margin-bottom: 1.4rem;
    }
    .mv-tips {
      border-top: 1px solid rgba(255,255,255,0.12);
      padding-top: 1rem;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.8rem;
    }
    .mv-tip {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.78rem;
      color: rgba(255,255,255,0.72);
      line-height: 1.45;
    }
    .mv-tip strong {
      display: block;
      color: var(--gold-light);
      font-size: 0.6rem;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      margin-bottom: 0.25rem;
    }

    /* ── Object lesson ───────────────────────── */
    .object-lesson-box {
      background: var(--parchment-2);
      border-left: 4px solid var(--gold);
      border-radius: 0 10px 10px 0;
      padding: 1.5rem 1.8rem;
      margin: 2rem 0;
    }
    .ol-item-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--gold);
      display: block;
      margin-bottom: 0.2rem;
    }
    .ol-item-name {
      font-family: 'Playfair Display', serif;
      font-size: 1.15rem;
      font-weight: 700;
      color: var(--forest);
      margin-bottom: 0.9rem;
    }
    .ol-how-to {
      font-size: 0.95rem;
      color: var(--ink-mid);
      margin-bottom: 0.6rem;
    }
    .ol-connection {
      font-style: italic;
      color: var(--rust);
      font-size: 0.95rem;
    }

    /* ── Parental guide ──────────────────────── */
    .age-panels {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
      margin-top: 1rem;
    }
    .age-panel {
      background: white;
      border-radius: 10px;
      padding: 1.3rem 1.2rem;
      border-top: 3px solid;
    }
    .age-panel.little-ones { border-color: #A0C878; }
    .age-panel.children    { border-color: #6BA8D4; }
    .age-panel.tweens      { border-color: #C47A5A; }
    .age-badge {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      margin-bottom: 0.6rem;
      display: block;
    }
    .age-panel.little-ones .age-badge { color: #5A8A30; }
    .age-panel.children    .age-badge { color: #2A6090; }
    .age-panel.tweens      .age-badge { color: #8A3A20; }
    .age-panel p { font-size: 0.92rem; color: var(--ink-mid); line-height: 1.6; }

    /* ── Wonder questions ────────────────────── */
    .wonder-grid { display: flex; flex-direction: column; gap: 0.85rem; margin-top: 1rem; }
    .wonder-item {
      display: grid;
      grid-template-columns: 70px 1fr;
      gap: 0.9rem;
      align-items: start;
      background: white;
      border-radius: 10px;
      padding: 1rem 1.2rem;
      border: 1px solid var(--divider);
    }
    .wonder-badge {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--sage);
      padding-top: 0.1rem;
      line-height: 1.4;
    }
    .wonder-q {
      font-style: italic;
      color: var(--ink-mid);
      font-size: 1rem;
      line-height: 1.55;
    }

    /* ── Family application ──────────────────── */
    .app-card {
      background: white;
      border-radius: 12px;
      padding: 1.4rem 1.6rem;
      margin-bottom: 1rem;
      border: 1px solid var(--divider);
    }
    .app-card-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--sage);
      margin-bottom: 0.35rem;
      display: block;
    }
    .app-card p, .app-card li { color: var(--ink-mid); font-size: 0.97rem; }
    .app-card ul { padding-left: 1.2rem; }
    .app-card li { margin-bottom: 0.3rem; }
    .table-question-text {
      font-family: 'Playfair Display', serif;
      font-style: italic;
      font-size: 1.12rem;
      color: var(--forest);
      line-height: 1.5;
    }
    .challenge-text {
      font-family: 'Crimson Text', serif;
      font-size: 1rem;
      color: var(--ink-mid);
    }
    .prayer-box {
      background: var(--forest);
      color: white;
      border-radius: 12px;
      padding: 1.6rem 1.8rem;
      margin-top: 1rem;
    }
    .prayer-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--gold-light);
      display: block;
      margin-bottom: 0.8rem;
    }
    .prayer-text {
      font-family: 'Crimson Text', serif;
      font-style: italic;
      font-size: 1.05rem;
      color: rgba(255,255,255,0.9);
      line-height: 1.7;
    }

    /* ── Parental blessing ───────────────────── */
    .blessing-box {
      text-align: center;
      padding: 2.5rem 2rem;
      border-top: 1px solid var(--divider);
      border-bottom: 1px solid var(--divider);
      margin: 2.5rem 0;
    }
    .blessing-crown { font-size: 1.6rem; display: block; margin-bottom: 0.9rem; }
    .blessing-label {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--sage);
      display: block;
      margin-bottom: 1rem;
    }
    .blessing-text {
      font-family: 'Playfair Display', serif;
      font-style: italic;
      font-size: 1.25rem;
      color: var(--forest);
      line-height: 1.6;
      max-width: 540px;
      margin: 0 auto;
    }

    /* ── Closing thought ─────────────────────── */
    .closing-thought {
      text-align: center;
      padding: 2rem 1rem 1rem;
    }
    .closing-thought blockquote {
      font-family: 'Playfair Display', serif;
      font-style: italic;
      font-size: 1.35rem;
      color: var(--forest);
      line-height: 1.5;
      max-width: 560px;
      margin: 0 auto 0.6rem;
    }
    .closing-thought blockquote::before { content: '\\201C'; }
    .closing-thought blockquote::after  { content: '\\201D'; }

    /* ── Footer ──────────────────────────────── */
    footer {
      text-align: center;
      padding: 2rem;
      border-top: 1px solid var(--divider);
      margin-top: 3rem;
    }
    .footer-brand {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.62rem;
      font-weight: 600;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--ink-light);
    }

    /* ── Download button (hidden on print) ───── */
    .download-bar {
      background: var(--forest);
      text-align: center;
      padding: 1rem;
    }
    .download-bar button {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      background: var(--gold);
      color: white;
      border: none;
      border-radius: 8px;
      padding: 0.65rem 2rem;
      cursor: pointer;
      margin: 0 0.5rem;
    }
    .download-bar button:hover { background: var(--gold-light); }
    .download-bar .hint {
      font-family: 'Montserrat', sans-serif;
      font-size: 0.65rem;
      color: rgba(255,255,255,0.5);
      margin-top: 0.5rem;
    }

    /* ── Print ───────────────────────────────── */
    @media print {
      body { font-size: 11pt; background: white; orphans: 3; widows: 3; }
      .download-bar { display: none !important; }

      /* Colour-accurate blocks */
      .cover,
      .memory-verse-box, .prayer-box,
      .age-panel { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

      /*
       * STRUCTURAL FIX: .section-header wraps the label + h2 together.
       * break-inside:avoid on a real block element is honoured by Chrome;
       * break-after:avoid on a bare <span> is not.
       */
      .section-header {
        break-inside: avoid;
        page-break-inside: avoid;
        break-after: avoid;
        page-break-after: avoid;
      }

      /* Card-level labels (object lesson, memory verse, prayer, etc.) */
      .ol-item-label, .mv-label, .prayer-label,
      .app-card-label, .blessing-label {
        break-after: avoid;
        page-break-after: avoid;
      }
      .ol-item-name, h3.sub-title {
        break-before: avoid;
        page-break-before: avoid;
        break-after: avoid;
        page-break-after: avoid;
      }

      /* Keep self-contained blocks together */
      .age-panel, .wonder-item, .app-card, .blessing-box, .closing-thought {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .age-panels {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      /*
       * TOP PADDING on blocks that may start a new page.
       * Must be padding (not margin) — margins collapse to 0 at page breaks,
       * padding is preserved by the CSS Fragmentation spec.
       */
      section, .memory-verse-box, .object-lesson-box,
      .wonder-grid, .blessing-box {
        padding-top: 0.5cm;
      }
      .ornament-divider { margin: 0.8cm 0; }

      @page { margin: 2.2cm 2cm; }
    }

    /* ── Responsive ──────────────────────────── */
    @media (max-width: 620px) {
      .age-panels { grid-template-columns: 1fr; }
      .mv-tips { grid-template-columns: 1fr; }
      .cover h1 { font-size: 1.7rem; }
      .page { padding: 2rem 1.2rem 3rem; }
      .wonder-item { grid-template-columns: 1fr; gap: 0.3rem; }
    }
  </style>
</head>
<body>

  <!-- Download bar -->
  <div class="download-bar">
    <button onclick="window.print()">Print / Save as PDF</button>
    <div class="hint">In the print dialog, choose "Save as PDF" and enable "Background graphics"</div>
  </div>

  <!-- Cover -->
  <div class="cover">
    <div class="cover-label">✦ &nbsp; SermonBuild &nbsp; ✦</div>
    <span class="cover-ornament">Family Devotional Guide</span>
    <h1>${esc(devotionalTitle)}</h1>
    <p class="cover-theme">${esc(theme)}</p>
    <p class="cover-verse">${esc(keyVerse)}</p>
    <p class="cover-meta">
      ${esc(sermonTitle)}
      ${displayDate ? `<span>·</span> ${esc(displayDate)}` : ''}
      ${scripture ? `<span>·</span> ${esc(scripture)}` : ''}
    </p>
  </div>

  <div class="page">

    <!-- Main Devotion -->
    <section>
      <div class="section-header">
        <span class="section-label">This Week's Devotion</span>
        <h2 class="section-title">${esc(devotionalTitle)}</h2>
      </div>
      <div class="devotion-body">${paragraphs(mainDevotion)}</div>
    </section>

    <div class="ornament-divider">✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦</div>

    <!-- Memory Verse -->
    <div class="memory-verse-box">
      <span class="mv-label">✦ &nbsp; Memory Verse</span>
      <blockquote>${esc(memoryVerse.text)}</blockquote>
      <cite>${esc(memoryVerse.reference)}</cite>
      <div class="mv-tips">
        <div class="mv-tip">
          <strong>🤲 Hand Motions</strong>
          ${esc(memoryVerse.motionTip)}
        </div>
        <div class="mv-tip">
          <strong>🎵 Melody / Rhythm</strong>
          ${esc(memoryVerse.melodyHint)}
        </div>
      </div>
    </div>

    <!-- Object Lesson -->
    <div class="object-lesson-box">
      <span class="ol-item-label">Object Lesson</span>
      <div class="ol-item-name">${esc(objectLesson.item)}</div>
      <p class="ol-how-to">${esc(objectLesson.howTo)}</p>
      <p class="ol-connection">→ ${esc(objectLesson.connection)}</p>
    </div>

    <div class="ornament-divider">✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦</div>

    <!-- Parental Guide -->
    <section>
      <div class="section-header">
        <span class="section-label">For Parents</span>
        <h2 class="section-title">Understanding Together</h2>
      </div>
      <div class="age-panels">
        <div class="age-panel little-ones">
          <span class="age-badge">Little Ones · Ages 3–6</span>
          <p>${esc(parentalGuide.littleOnes)}</p>
        </div>
        <div class="age-panel children">
          <span class="age-badge">Children · Ages 7–11</span>
          <p>${esc(parentalGuide.children)}</p>
        </div>
        <div class="age-panel tweens">
          <span class="age-badge">Tweens · Ages 12+</span>
          <p>${esc(parentalGuide.tweens)}</p>
        </div>
      </div>
    </section>

    <div class="ornament-divider">✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦</div>

    <!-- Wonder Questions -->
    <section>
      <div class="section-header">
        <span class="section-label">Discussion</span>
        <h2 class="section-title">Wonder Together</h2>
      </div>
      <div class="wonder-grid">
        ${wonderRows}
      </div>
    </section>

    <div class="ornament-divider">✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦</div>

    <!-- Family Application -->
    <section>
      <div class="section-header">
        <span class="section-label">Living It Out</span>
        <h2 class="section-title">Family Application</h2>
      </div>

      <div class="app-card">
        <span class="app-card-label">🍽 &nbsp; Dinner Table Question</span>
        <p class="table-question-text">${esc(familyApplication.tableQuestion)}</p>
      </div>

      <div class="app-card">
        <span class="app-card-label">🎯 &nbsp; Weekly Family Challenge</span>
        <p class="challenge-text">${esc(familyApplication.weeklyChallenge)}</p>
      </div>

      <div class="app-card">
        <span class="app-card-label">💬 &nbsp; Conversation Starters</span>
        <ul>${starters}</ul>
      </div>

      <div class="prayer-box">
        <span class="prayer-label">🙏 &nbsp; Family Prayer</span>
        <p class="prayer-text">${esc(familyApplication.prayerGuide)}</p>
      </div>
    </section>

    <div class="ornament-divider">✦ &nbsp;─────&nbsp; ✦ &nbsp;─────&nbsp; ✦</div>

    <!-- Parental Blessing -->
    <div class="blessing-box">
      <span class="blessing-crown">✦</span>
      <span class="blessing-label">Parental Blessing</span>
      <p class="blessing-text">${esc(parentalBlessing)}</p>
    </div>

    <!-- Closing Thought -->
    <div class="closing-thought">
      <blockquote>${esc(closingThought)}</blockquote>
    </div>

  </div>

  <!-- Footer -->
  <footer>
    <span class="footer-brand">SermonBuild &nbsp;·&nbsp; Family Devotional Guide</span>
  </footer>

</body>
</html>`;
}

export function downloadDevotionalHTML(data: DevotionalData): void {
  const html = generateDevotionalHTML(data);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (data.devotionalTitle || data.sermonTitle || 'devotional')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  a.download = `family-devotional-${safeName}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
